import { tz } from "@date-fns/tz";
import {
  AnalyticEvent,
  formatDurationToDays,
  formatPrice,
  formatReservationDate,
  formatReservationOffset,
  isWeb,
  NoSubscriptionOptionInBundle,
  upperFirst,
  useEstimateProcessingFeeQuery,
  useGetReservationByIdQuery,
  useGetVirtualWalletBalanceQuery,
  usePostHog,
  useToday,
  useV2GetBundleByIdQuery,
} from "@stepapp/core";
import { captureSentryException } from "@stepapp/sentry";
import {
  Block,
  Button,
  Divider,
  Gradient,
  gradientPoint,
  Image,
  Loader,
  OrderInfoItem,
  palette,
  SvgIcon,
  spacing,
  Typography,
  useBreakpoints,
} from "@stepapp/ui";
import { addMinutes, differenceInHours, isPast } from "date-fns";
import { Link, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet } from "react-native";
import { zigzagDivider } from "../../../src/assets/images";
import {
  AverageCleaningTime,
  ModifyReservationSheet,
  Payment,
  ReservationNote,
  StripeSafePayment,
  WorkerWithStatus,
} from "../../../src/components";
import ErrorPage from "../../../src/components/ErrorPage";
import CancelReservationSheet from "../../../src/components/Modal/CancelReservationSheet";
import { useAppLinks, useAppSelector, useRefreshControl } from "../../../src/hooks";
import { AppStorage } from "../../../src/services/AppStorage";
import { ReservationCompletedStatuses } from "../../../src/store/types";
import { smallPageWrapper } from "../../../src/styles/smallPageWrapper";
import AdditionalServices from "./AdditionalServices";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: refactor
const ReservationDetails = () => {
  const postHog = usePostHog();
  const today = useToday();
  const { t } = useTranslation();
  const { navigate, replace } = useRouter();
  const { reservationId = "" } = useLocalSearchParams<{ reservationId: string }>();
  const [questionToEdit, setQuestionToEdit] = useState("");
  const [showCancellationDialog, setShowCancellationDialog] = useState(false);
  const { sm } = useBreakpoints();
  const userId = useAppSelector(({ user }) => user.id);
  const priceFormatter = formatPrice(AppStorage().get("countryCode"));
  const { CancellationRules } = useAppLinks();
  const configTimezone = useAppSelector(({ appConfig }) => appConfig.timezone);

  const {
    data,
    loading,
    refetch: refetchReservation,
  } = useGetReservationByIdQuery({
    variables: { orderId: reservationId, isCustomerApp: true },
    onError(error) {
      captureSentryException(
        "[ReservationDetails error]: error while fetching reservation details from query getReservationById",
        {
          extra: {
            userId,
            reservationId,
            errorMessage: error?.message,
          },
        },
      );
    },
  });

  const reservation = data?.getReservationById;

  const { data: bundle } = useV2GetBundleByIdQuery({
    skip: !reservation?.bundle?.id,
    variables: {
      id: reservation?.bundle?.id || "",
    },
  });

  const [refreshControl] = useRefreshControl(refetchReservation);
  const oneTimeService = !formatDurationToDays(
    reservation?.subscription?.periodicity?.cycleDuration || "P0D",
  );
  const editServiceHours = useMemo(() => (oneTimeService ? 48 : 36), [oneTimeService]);

  const startDateDay = useMemo(
    () =>
      formatReservationOffset(reservation?.startDate || "", reservation?.subscription?.startDate),
    [reservation?.startDate, reservation?.subscription?.startDate],
  );
  const isPastReservation = useMemo(() => isPast(startDateDay), [startDateDay]);
  const isCompleted =
    isPastReservation ||
    (reservation?.status ? ReservationCompletedStatuses.includes(reservation?.status) : true);

  const isServiceEditable = useMemo(
    () => differenceInHours(startDateDay, today, { in: tz(configTimezone) }) >= 24 && !isCompleted,
    [startDateDay, isCompleted, today, configTimezone],
  );
  const canChangePayment = !isCompleted && reservation?.payment?.paymentMethodId !== "cash";

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    postHog?.capture(AnalyticEvent.Reservation.View, {
      id: reservationId,
      name: reservation?.bundle.name || "",
      price: reservation?.price || 0,
    });
  }, [postHog]);

  const { data: processingFee } = useEstimateProcessingFeeQuery({
    variables: { reservationId },
    skip: isCompleted,
  });
  const { data: virtualWalletBalance } = useGetVirtualWalletBalanceQuery({
    skip: isCompleted,
  });

  const virtualBalance = virtualWalletBalance?.getVirtualWalletBalance.balance || 0;
  const priceAfterDiscount =
    (reservation?.price || 0) - (reservation?.shouldUseDiscount ? virtualBalance : 0);

  const cancellationFee = priceFormatter(processingFee?.estimateProcessingFee?.amount, {
    currency: processingFee?.estimateProcessingFee?.currency,
  });

  const date = useMemo(() => {
    if (loading) return "";

    return `${upperFirst(formatReservationDate(startDateDay, "EEEE, d MMMM"))} ${t(
      "common.at",
    )} ${formatReservationDate(startDateDay, "HH:mm")}-${formatReservationDate(addMinutes(startDateDay, 15), "HH:mm")}`;
  }, [loading, startDateDay, t]);

  const canEditWithoutCharge = useMemo(
    () => !processingFee?.estimateProcessingFee?.amount,
    [processingFee?.estimateProcessingFee?.amount],
  );

  const includedServiceIds = useMemo(
    () => reservation?.services.map((service) => service?.service?.id),
    [reservation?.services],
  );
  const canAddMoreServices =
    (bundle?.v2GetBundleById?.services?.filter(
      (service) => !includedServiceIds?.includes(service?.id),
    )?.length || 0) > 0;
  const isCustomerNoteVisible = (reservation?.customerNote?.length || 0) > 0;

  const periodicityTitle =
    reservation?.periodicity?.title !== NoSubscriptionOptionInBundle
      ? reservation?.periodicity?.title
      : t("screens.serviceDetails.type_oneTime");

  const handleQuestionEdit = useCallback(
    (editQuestionDetails: string) => {
      if (isWeb) {
        return setQuestionToEdit(editQuestionDetails);
      }

      navigate({
        pathname: `/calendar/${reservationId}/edit-reservation`,
        params: { questionToEdit: editQuestionDetails },
      });
    },
    [navigate, reservationId],
  );

  const handleEditNote = useCallback(() => {
    navigate({
      pathname: `/calendar/${reservationId}/edit-reservation`,
      params: { questionToEdit: "customerNote" },
    });
  }, [navigate, reservationId]);

  if (loading) {
    return (
      <Block alignCenter justifyCenter flex={1}>
        <Loader />
      </Block>
    );
  }

  if (!data?.getReservationById) {
    return (
      <ErrorPage
        title={t("screens.service.reservationError")}
        description={t("screens.serviceDetails.fetchReservationError")}
        onClick={() => replace("/calendar")}
      />
    );
  }

  return (
    <>
      <ScrollView refreshControl={refreshControl()}>
        <Block style={isWeb && smallPageWrapper}>
          <Block style={styles.container}>
            <Block paddingBottom={1} paddingTop={isWeb ? 3 : 2}>
              <Typography variant="largeTitle">{date}</Typography>
            </Block>

            <>
              <Block justifySpace row marginBottom={1}>
                {isCompleted ? (
                  <Block />
                ) : (
                  <Button
                    label={t("screens.serviceDetails.changeDate", {
                      fee: canEditWithoutCharge ? "" : cancellationFee,
                    })}
                    onPress={() => handleQuestionEdit("date")}
                    style={styles.changeDate}
                    type="accentLink"
                  />
                )}
                <Block alignCenter row>
                  <SvgIcon
                    color={palette.primary.pinkSalmon}
                    name={oneTimeService ? "additional" : "subscription"}
                    size={18}
                  />
                  <Typography style={styles.subscriptionType} type="secondary" variant="lightLabel">
                    {oneTimeService
                      ? t("screens.serviceDetails.type_oneTime")
                      : t("screens.serviceDetails.type_subscription")}
                  </Typography>
                </Block>
              </Block>

              {isServiceEditable &&
                (canEditWithoutCharge ? (
                  <Typography type="secondary" variant="lightLabel">
                    {t("screens.serviceDetails.changeDateInfo", {
                      hoursBeforeFee: editServiceHours,
                    })}
                  </Typography>
                ) : (
                  <Block height={sm ? 14 : 18} marginVertical={1}>
                    <Block row padding={1.5} style={styles.subscriptionChangeDateInfo}>
                      <SvgIcon color={palette.common.white} name="info-filled" />
                      <Block flex={1} marginLeft={1.5}>
                        <Typography type="white" variant="newBodyBold">
                          {t("screens.serviceDetails.subscriptionChangeDateInfo")}
                        </Typography>
                      </Block>
                    </Block>
                    <Gradient
                      direction="topRight"
                      height={sm ? 110 : 140}
                      points={gradientPoint.pink}
                      style={styles.gradient}
                    />
                  </Block>
                ))}

              {reservation?.workers && reservation?.workers?.length > 0 && (
                <>
                  <Block marginBottom={isServiceEditable ? 3 : 0} marginTop={1.5}>
                    <WorkerWithStatus
                      orderId={reservation?.id}
                      workers={reservation?.workers}
                      hideActions={isCompleted}
                    />
                  </Block>
                  {isServiceEditable && <Divider spaceless />}
                </>
              )}

              {!isCompleted && (
                <Block paddingTop={isServiceEditable ? 3 : 1.5}>
                  <Button
                    label={
                      canEditWithoutCharge
                        ? t("screens.serviceDetails.cancelService")
                        : `${t("screens.serviceDetails.cancelService")} ${t(
                            "screens.serviceDetails.fee",
                            { fee: cancellationFee },
                          )}`
                    }
                    onPress={() => setShowCancellationDialog(true)}
                    type="primary"
                  />
                  <Block paddingTop={2}>
                    <Typography center type="secondary" variant="lightLabel">
                      {t("screens.serviceDetails.cancellationRule", {
                        hoursBeforeFee: editServiceHours,
                      })}
                    </Typography>
                  </Block>

                  <Link
                    href={CancellationRules}
                    style={styles.showCancellationRulesLink}
                    target="_blank"
                  >
                    <Typography type="accent" variant="bodyBold" decoration="underline">
                      {t("screens.serviceDetails.showCancellationRules")}
                    </Typography>
                  </Link>
                </Block>
              )}
            </>

            {isCustomerNoteVisible && (
              <ReservationNote
                note={reservation?.customerNote}
                containerStyles={styles.reservationNote}
                onPress={isPastReservation ? undefined : handleEditNote}
                categoryType={bundle?.v2GetBundleById?.categoryType}
              />
            )}

            <Divider />

            <Block paddingTop={2}>
              <Typography type="secondary" variant="paragraph">
                {t("screens.serviceDetails.service")}
              </Typography>

              <Block paddingBottom={3}>
                <Typography decoration="capitalize" type="primary" variant="title">
                  {reservation?.bundle.name}
                </Typography>
              </Block>

              <OrderInfoItem
                answer={periodicityTitle}
                title={t("screens.serviceSummary.howOften")}
                type="unstyled"
              />
              <Divider spaceless />

              <Block marginVertical={1.5}>
                <AdditionalServices
                  attributes={reservation?.attributes}
                  handleQuestionEdit={handleQuestionEdit}
                  isEditable={isServiceEditable}
                  services={reservation?.services}
                />
              </Block>

              {isServiceEditable && canAddMoreServices ? (
                <Button
                  label={t("screens.serviceDetails.addMoreServices")}
                  onPress={() => handleQuestionEdit("services")}
                  type="secondary"
                />
              ) : (
                <Divider spaceless />
              )}

              <AverageCleaningTime
                timeConsumption={reservation?.timeConsumption}
                marginVertical={2}
                categoryType={reservation?.bundle?.categoryType}
              />

              {reservation?.shouldUseDiscount && !!virtualBalance && (
                <>
                  <Block justifySpace row marginTop={2}>
                    <Typography variant="bodyBold">{t("common.together")}</Typography>
                    <Typography variant="body">{priceFormatter(reservation?.price)}</Typography>
                  </Block>
                  <Block justifySpace row marginTop={1}>
                    <Typography variant="bodyBold">
                      {t("pages.serviceSummary.yourStepappWallet")}
                    </Typography>
                    <Typography type="error" variant="body">
                      -
                      {priceFormatter(
                        virtualBalance > (reservation?.price || 0)
                          ? reservation?.price
                          : virtualBalance,
                      )}
                    </Typography>
                  </Block>
                </>
              )}

              <Block alignCenter justifySpace row marginVertical={1}>
                <Typography variant="sectionTitle">{t("screens.serviceSummary.toPay")}</Typography>

                <Typography variant="sectionTitle">
                  {priceFormatter(priceAfterDiscount >= 0 ? priceAfterDiscount : 0, {
                    includeRest: true,
                  })}
                </Typography>
              </Block>

              <Typography type="secondary" variant="lightLabel">
                {t("screens.serviceDetails.totalPaymentInfo")}
              </Typography>
            </Block>

            <Image contentFit="contain" source={zigzagDivider} style={styles.zigzagDivider} />

            <Block paddingBottom={2}>
              {canChangePayment && (
                <>
                  <Payment
                    bundleName={reservation?.bundle.name}
                    cycleDuration={reservation?.subscription?.periodicity?.cycleDuration}
                    paymentId={reservation?.payment?.id || ""}
                    paymentMethodId={reservation?.payment?.paymentMethodId || ""}
                    price={reservation?.price}
                    refetchReservation={refetchReservation}
                    startDate={reservation?.startDate}
                    categoryType={reservation?.bundle?.categoryType}
                  />

                  <StripeSafePayment />
                </>
              )}

              <Block marginBottom={1.5} marginTop={4}>
                <Typography type="primary" variant="sectionTitle">
                  {t("screens.serviceDetails.orderFor")}
                </Typography>
              </Block>
              <OrderInfoItem
                homeSize={reservation?.address?.floorArea || 0}
                homeType={reservation?.address?.type}
                title={`${t("screens.myHome.st")}. ${reservation?.address?.streetAddress1} ${
                  reservation?.address?.streetAddress2 || ""
                }`}
                titleContinue={`${reservation?.address?.postalOrZipCode} ${reservation?.address?.city}`}
                type="address"
              />
            </Block>
          </Block>
        </Block>
      </ScrollView>

      <ModifyReservationSheet
        reservationId={reservationId}
        isVisible={!!questionToEdit}
        setQuestionToEdit={setQuestionToEdit}
        questionToEdit={questionToEdit}
      />

      <CancelReservationSheet
        startDate={startDateDay}
        isVisible={showCancellationDialog}
        reservationId={reservationId}
        subscriptionId={data?.getReservationById?.subscription?.id}
        toggle={() => setShowCancellationDialog(false)}
        oneTimeService={oneTimeService}
        cancellationFee={cancellationFee}
        handleQuestionEdit={handleQuestionEdit}
      />
    </>
  );
};

export default ReservationDetails;

const styles = StyleSheet.create({
  additionalInfoTitle: {
    marginBottom: spacing.unit * 1.5,
  },
  changeDate: {
    alignItems: "flex-start",
  },
  container: {
    margin: "auto",
    maxWidth: spacing.unit * 72.5,
    paddingBottom: spacing.unit * 10,
    width: "100%",
  },
  gradient: {
    borderRadius: 5,
  },
  reservationNote: {
    marginVertical: spacing.unit * 2,
  },
  showCancellationRulesLink: {
    alignSelf: "center",
    paddingVertical: spacing.unit * 2,
  },
  subscriptionChangeDateInfo: {
    zIndex: 1,
  },
  subscriptionType: {
    marginLeft: 4,
  },
  zigzagDivider: {
    height: spacing.unit * 1.65,
    marginHorizontal: spacing.unit * -2.5,
    marginTop: spacing.unit * 4.5,
    marginBottom: spacing.unit * 2,
  },
});
